import { Rng } from "./rng";

/**
 * تخته‌ی «فرارِ فلش‌ها»: هر فلش چند خانه‌ی پشتِ‌هم است (صاف یا خمیده) و نوکش رو به سوی گامِ آخر.
 * لمس که شود، اگر سرِ راهِ نوک تا لبه‌ی تخته خالی باشد بیرون می‌رود؛ وگرنه به مانع می‌خورد.
 *
 * برداشتنِ فلش هیچ‌وقت راهِ فلشِ دیگری را نمی‌بندد، پس هر فلشِ آزاد را هر وقت بشود برداشت و ترتیب
 * مهم نیست (docs/arrow-race.md). سازنده هم فقط تخته‌ای می‌دهد که با همین برداشتنِ ساده خالی شود.
 *
 * این‌ها آینه‌ی قاعده‌ی سرورِ آینده‌اند (boards/arrows): هر تغییری باید آن‌جا و در C# هم برود.
 */

/** سوی نوک: ۰ بالا، ۱ راست، ۲ پایین، ۳ چپ (y رو به پایین). */
export type Dir = 0 | 1 | 2 | 3;
export const DX = [0, 1, 0, -1] as const;
export const DY = [-1, 0, 1, 0] as const;

export interface Arrow {
  id: number;
  /** خانه‌ها (y*w + x) از دُم تا نوک؛ دست‌کم دو خانه. */
  cells: number[];
  /** تخته‌ی مشترک: صاحبِ فلش (رنگِ بازیکن). تخته‌ی تک‌نفره ندارد. */
  owner?: number;
}

export interface Board {
  w: number;
  h: number;
  arrows: Arrow[];
  /** تخته‌ی مشترک: نقشِ وسط که فلش ندارد (شمسه، ترنج، تنِ پروانه)؛ چهارگوشِ دورش به واحدِ خانه. */
  hub?: { x: number; y: number; w: number; h: number; kind: "shamseh" | "toranj" | "body" };
}

export function headDir(b: Board, a: Arrow): Dir {
  return dirOf(b.w, a.cells);
}

export function dirOf(w: number, cells: readonly number[]): Dir {
  const head = cells[cells.length - 1]!, prev = cells[cells.length - 2]!;
  const dx = (head % w) - (prev % w);
  const dy = Math.floor(head / w) - Math.floor(prev / w);
  return dx === 1 ? 1 : dx === -1 ? 3 : dy === 1 ? 2 : 0;
}

/** خانه‌های سرِ راهِ نوک تا لبه‌ی تخته، به ترتیب. */
export function ray(b: Board, a: Arrow): number[] {
  return rayOf(b.w, b.h, a.cells, headDir(b, a));
}

export function rayOf(w: number, h: number, cells: readonly number[], d: Dir): number[] {
  const head = cells[cells.length - 1]!;
  let x = (head % w) + DX[d], y = Math.floor(head / w) + DY[d];
  const out: number[] = [];
  while (x >= 0 && y >= 0 && x < w && y < h) {
    out.push(y * w + x);
    x += DX[d];
    y += DY[d];
  }
  return out;
}

/** occ[خانه] = شماره‌ی فلشِ زنده‌ی رویش، یا −۱. */
export function occupancy(b: Board, alive: readonly boolean[]): Int16Array {
  const occ = new Int16Array(b.w * b.h).fill(-1);
  for (const a of b.arrows) if (alive[a.id]) for (const c of a.cells) occ[c] = a.id;
  return occ;
}

/** چند خانه تا اولین مانع (۱ = خانه‌ی کناری)؛ ۰ = راه باز است. */
export function blockedAt(b: Board, occ: Int16Array, a: Arrow): number {
  const r = ray(b, a);
  for (let i = 0; i < r.length; i++) if (occ[r[i]!]! >= 0) return i + 1;
  return 0;
}

/** فلش‌های زنده‌ای که همین حالا بیرون می‌روند. */
export function freeArrows(b: Board, alive: readonly boolean[]): number[] {
  const occ = occupancy(b, alive);
  return b.arrows.filter((a) => alive[a.id] && blockedAt(b, occ, a) === 0).map((a) => a.id);
}

/**
 * موج‌ها: چند بار باید همه‌ی فلش‌های آزاد را با هم برداشت تا تخته خالی شود — سنجه‌ی سختی.
 * −۱ = جایی گیر می‌کند (سازنده هرگز چنین تخته‌ای نمی‌دهد).
 */
export function waves(b: Board): number {
  const alive = b.arrows.map(() => true);
  let left = b.arrows.length, n = 0;
  while (left > 0) {
    const free = freeArrows(b, alive);
    if (!free.length) return -1;
    for (const id of free) alive[id] = false;
    left -= free.length;
    n++;
  }
  return n;
}

/**
 * پله‌ی تخته. تخته روی بومِ بی‌انتها کشیده و جابه‌جا و بزرگ می‌شود (کاربر، ۲۹ اکتبر)، پس سقفِ ۸ ستون ِ
 * طرحِ اول برداشته شد. اندازه به مسابقه بسته است، نه به دستگاه، تا همه یک تخته داشته باشند.
 * fill: سهمِ خانه‌هایی که سازنده می‌کوشد پر کند. turn: احتمالِ پیچیدنِ فلش در هر گام.
 */
export interface Tier {
  w: number;
  h: number;
  minLen: number;
  maxLen: number;
  fill: number;
  turn: number;
}

export const TIERS: readonly Tier[] = [
  { w: 8, h: 10, minLen: 2, maxLen: 5, fill: 0.92, turn: 0.35 },
  { w: 10, h: 13, minLen: 2, maxLen: 7, fill: 0.93, turn: 0.4 },
  { w: 12, h: 16, minLen: 3, maxLen: 9, fill: 0.94, turn: 0.45 },
];

/** بذرِ تخته‌ی iام ِ یک مسابقه: هر مسابقه سه تخته‌ی جدا دارد و هر تخته از بذرِ خودش ساخته می‌شود. */
export function boardSeed(raceSeed: number, i: number): number {
  return raceSeed * 3 + i;
}

/**
 * تخته‌ی پله‌ی tier از بذرِ seed. همان بذر همیشه همان تخته را می‌دهد.
 *
 * اول تخته را با فلش‌های تصادفی پر می‌کند، بی هیچ شرطی روی راهشان — پس بیشترشان از اول بسته‌اند،
 * مثلِ تخته‌های بازیِ اصلی. بعد [[repair]] گره‌ها را باز می‌کند تا تخته حل‌شدنی شود.
 */
export function generate(seed: number, tier: number): Board {
  const t = TIERS[tier]!;
  const rng = new Rng(seed);
  return pack(t, shape(t, rng), rng, () => true);
}

/**
 * خانه‌های ok را با فلش پر می‌کند و گره‌ها را باز می‌کند. allow می‌گوید فلشی با این خانه‌ها (دُم تا نوک)
 * مجاز است یا نه — تخته‌ی مشترک با آن نمی‌گذارد نوکِ فلشی رو به رنگِ دیگری باشد.
 */
export function pack(t: Tier, ok: readonly boolean[], rng: Rng,
  allow: (cells: readonly number[]) => boolean): Board {
  const bad = (c: readonly number[]) => crossesSelf(t, c) || !allow(c);
  const occ = new Int16Array(t.w * t.h).fill(-1);
  const list: number[][] = [];
  const target = Math.floor(ok.filter(Boolean).length * t.fill);
  let filled = 0;
  for (let tries = 0; filled < target && tries < ok.length * 6; tries++) {
    const empty: number[] = [];
    for (let i = 0; i < ok.length; i++) if (ok[i] && occ[i]! < 0) empty.push(i);
    if (!empty.length) break;
    const start = empty[rng.next(empty.length)]!;
    const len = t.minLen + rng.next(t.maxLen - t.minLen + 1);
    const path = walk(t, ok, occ, start, len, rng);
    if (path.length < 2) continue;
    // نوک روی یکی از دو سر؛ سویی که راهِ نوکش از تنِ خودش بگذرد هرگز آزاد نمی‌شود.
    let cells = rng.chance(0.5) ? path : path.slice().reverse();
    if (bad(cells)) cells = cells.slice().reverse();
    if (bad(cells)) continue;
    for (const c of cells) occ[c] = list.length;
    list.push(cells);
    filled += cells.length;
  }
  return repair(t, list, rng, allow);
}

/**
 * گره‌گشایی: برداشتنِ ساده را شبیه‌سازی می‌کند و هر جا گیر کرد (فلش‌هایی که در حلقه پشتِ هم‌اند)،
 * یکی از گیرکرده‌ها را برمی‌گرداند، اگر برگرداندن راهش را باز کند؛ وگرنه یکی را برمی‌دارد.
 *
 * برگرداندن خانه‌ها را عوض نمی‌کند و برداشتن فقط خانه خالی می‌کند، پس برداشتن‌های قبلی درست
 * می‌مانند و شبیه‌سازی از همان‌جا ادامه می‌یابد. تخته‌ی نهایی با همین ترتیب خالی می‌شود.
 */
function repair(t: Pick<Tier, "w" | "h">, list: number[][], rng: Rng, allow: (cells: readonly number[]) => boolean): Board {
  const b: Board = { w: t.w, h: t.h, arrows: list.map((cells, id) => ({ id, cells })) };
  const alive = b.arrows.map(() => true);
  const gone = b.arrows.map(() => false);
  let left = b.arrows.length;
  while (left > 0) {
    const free = freeArrows(b, alive);
    if (free.length) {
      for (const id of free) alive[id] = false;
      left -= free.length;
      continue;
    }
    const stuck = b.arrows.filter((a) => alive[a.id]).map((a) => a.id);
    const occ = occupancy(b, alive);
    const flippable = stuck.filter((id) => {
      const cells = b.arrows[id]!.cells.slice().reverse();
      return allow(cells) && rayOf(t.w, t.h, cells, dirOf(t.w, cells)).every((c) => occ[c]! < 0);
    });
    if (flippable.length) {
      b.arrows[flippable[rng.next(flippable.length)]!]!.cells.reverse();
    } else {
      const id = stuck[rng.next(stuck.length)]!;
      alive[id] = false;
      gone[id] = true;
      left--;
    }
  }
  const arrows = b.arrows.filter((a) => !gone[a.id]).map((a, id) => ({ id, cells: a.cells }));
  return { w: t.w, h: t.h, arrows };
}

function crossesSelf(t: Pick<Tier, "w" | "h">, cells: readonly number[]): boolean {
  return rayOf(t.w, t.h, cells, dirOf(t.w, cells)).some((c) => cells.includes(c));
}

/** شکل‌های تخته: مستطیلِ گوشه‌بریده، بعلاوه، لوزی، بیضی و ستاره (بعلاوه‌ی باریک روی لوزی). */
export const SHAPES = 5;

/** شکلِ تخته. فقط خانه‌های true فلش می‌گیرند. */
function shape(t: Tier, rng: Rng): boolean[] {
  const kind = rng.next(SHAPES);
  const qx = Math.max(1, Math.floor(t.w / 4)), qy = Math.max(1, Math.floor(t.h / 4));
  const ok: boolean[] = [];
  for (let y = 0; y < t.h; y++) {
    for (let x = 0; x < t.w; x++) {
      // فاصله از مرکز، نسبت به نیم‌پهنا و نیم‌بلندا: ۰ وسط، ۱ لبه.
      const u = Math.abs(x + 0.5 - t.w / 2) / (t.w / 2), v = Math.abs(y + 0.5 - t.h / 2) / (t.h / 2);
      const cx = Math.min(x, t.w - 1 - x), cy = Math.min(y, t.h - 1 - y);
      let on: boolean;
      if (kind === 0) on = !(cx < 1 && cy < 1);
      else if (kind === 1) on = !(cx < qx && cy < qy);
      else if (kind === 2) on = u + v <= 1.15;
      else if (kind === 3) on = u * u + v * v <= 1.1;
      else on = u <= 0.36 || v <= 0.3 || u + v <= 0.95;
      ok.push(on);
    }
  }
  return ok;
}

/** راه‌رفتنِ تصادفی از start روی خانه‌های خالی، بی گذشتن از خودش؛ بیشتر صاف می‌رود و گاهی می‌پیچد. */
function walk(t: Tier, ok: readonly boolean[], occ: Int16Array, start: number, len: number, rng: Rng): number[] {
  const path = [start];
  const used = new Set(path);
  let dir = rng.next(4);
  while (path.length < len) {
    const cur = path[path.length - 1]!;
    const x = cur % t.w, y = Math.floor(cur / t.w);
    const opts: number[] = [];
    for (let d = 0; d < 4; d++) {
      const nx = x + DX[d]!, ny = y + DY[d]!;
      if (nx < 0 || ny < 0 || nx >= t.w || ny >= t.h) continue;
      const c = ny * t.w + nx;
      if (ok[c] && occ[c]! < 0 && !used.has(c)) opts.push(d);
    }
    if (!opts.length) break;
    // ترتیبِ صدا زدنِ rng بخشی از قاعده است: chance فقط وقتی که ادامه‌ی صاف ممکن است.
    if (!(opts.includes(dir) && !rng.chance(t.turn))) dir = opts[rng.next(opts.length)]!;
    const c = (y + DY[dir]!) * t.w + (x + DX[dir]!);
    path.push(c);
    used.add(c);
  }
  return path;
}
